<template>
    <Toast
        unstyled
        :pt="theme"
        :ptOptions="{
            mergeProps: ptViewMerge
        }"
    >
        <template #closeicon>
            <TimesIcon />
        </template>
        <template v-for="(_, slotName) in $slots" #[slotName]="slotProps">
            <slot :name="slotName" v-bind="slotProps ?? {}" />
        </template>
    </Toast>
</template>

<script setup lang="ts">
import TimesIcon from '@primevue/icons/times';
import Toast, { type ToastPassThroughOptions, type ToastProps } from 'primevue/toast';
import { ref } from 'vue';
import { ptViewMerge } from './utils';

interface Props extends /* @vue-ignore */ ToastProps {}
defineProps<Props>();

const theme = ref<ToastPassThroughOptions>({
    root: `w-96 rounded-md whitespace-pre-line break-words
        p-top-center:-translate-x-1/2 p-bottom-center:-translate-x-1/2
        p-center:min-w-[20vw] p-center:-translate-x-1/2 p-center:-translate-y-1/2`,
    message: `mb-4 not-p-custom:border not-p-custom:backdrop-blur-sm dark:not-p-custom:backdrop-blur-md not-p-custom:rounded-md
        p-info:bg-info/10 p-info:border-info/30 p-info:text-navy-900 dark:p-info:bg-info/20 dark:p-info:border-info/40 dark:p-info:text-white
        p-success:bg-positive/10 p-success:border-positive/30 p-success:text-navy-900 dark:p-success:bg-positive/20 dark:p-success:border-positive/40 dark:p-success:text-white
        p-warn:bg-warning/10 p-warn:border-warning/30 p-warn:text-navy-900 dark:p-warn:bg-warning/20 dark:p-warn:border-warning/40 dark:p-warn:text-white
        p-error:bg-negative/10 p-error:border-negative/30 p-error:text-navy-900 dark:p-error:bg-negative/20 dark:p-error:border-negative/40 dark:p-error:text-white
        p-secondary:bg-surface-100 p-secondary:border-surface-200 p-secondary:text-surface-600 dark:p-secondary:bg-surface-800 dark:p-secondary:border-surface-700 dark:p-secondary:text-surface-300
        p-contrast:bg-surface-900 p-contrast:border-surface-950 p-contrast:text-surface-50 dark:p-contrast:bg-surface-0 dark:p-contrast:border-surface-100 dark:p-contrast:text-surface-950`,
    messageContent: `flex items-start p-3 gap-2`,
    messageIcon: `flex-shrink-0 text-lg w-[1.125rem] h-[1.125rem] mt-1`,
    messageText: `flex-auto flex flex-col gap-2`,
    summary: `font-medium text-base`,
    detail: `font-medium text-sm text-surface-700 dark:text-surface-0
        p-contrast:text-surface-0 dark:p-contrast:text-surface-950`,
    buttonContainer: ``,
    closeButton: `flex items-center justify-center overflow-hidden relative cursor-pointer bg-transparent select-none
        transition-colors duration-200 text-inherit w-7 h-7 rounded-full -mt-[25%] -end-1/4 p-0 border-none
        focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2
        p-info:hover:bg-info/15 p-info:focus-visible:outline-info dark:p-info:hover:bg-white/5 dark:p-info:focus-visible:outline-info
        p-success:hover:bg-positive/15 p-success:focus-visible:outline-positive dark:p-success:hover:bg-white/5 dark:p-success:focus-visible:outline-positive
        p-warn:hover:bg-warning/15 p-warn:focus-visible:outline-warning dark:p-warn:hover:bg-white/5 dark:p-warn:focus-visible:outline-warning
        p-error:hover:bg-negative/15 p-error:focus-visible:outline-negative dark:p-error:hover:bg-white/5 dark:p-error:focus-visible:outline-negative
        p-secondary:hover:bg-surface-200 p-secondary:focus-visible:outline-surface-600 dark:p-secondary:hover:bg-surface-700 dark:p-secondary:focus-visible:outline-surface-300
        p-contrast:hover:bg-surface-800 p-contrast:focus-visible:outline-surface-50 dark:p-contrast:hover:bg-surface-100 dark:p-contrast:focus-visible:outline-surface-950`,
    closeIcon: `text-base w-4 h-4`,
    transition: {
        enterFromClass: 'opacity-0 translate-y-1/2',
        enterActiveClass: 'transition-all duration-500',
        leaveFromClass: 'max-h-[1000px]',
        leaveActiveClass: 'transition-all duration-500',
        leaveToClass: 'max-h-0 opacity-0 mb-0 overflow-hidden'
    }
});
</script>
